import type { FormEvent } from 'react';
import { Clock, Info, RotateCcw, Save } from 'lucide-react';
import type { TokenDurationUnit } from '../../../types/auth/TokenConfigurationTypes';
import { readableDuration, toMinutes } from './tokenDuration';

interface TokenConfigurationFormProps {
  value: number | '';
  unit: TokenDurationUnit;
  updatedAt: string | null;
  error: string | null;
  loading: boolean;
  onValueChange: (value: number | '') => void;
  onUnitChange: (unit: TokenDurationUnit) => void;
  onSubmit: () => void;
  onReset: () => void;
}

const formatUpdatedAt = (updatedAt: string | null) => {
  if (!updatedAt) return 'Sin actualizaciones registradas';

  return new Intl.DateTimeFormat('es-SV', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(updatedAt));
};

const TokenConfigurationForm = ({
  value,
  unit,
  updatedAt,
  error,
  loading,
  onValueChange,
  onUnitChange,
  onSubmit,
  onReset,
}: TokenConfigurationFormProps) => {
  const durationMinutes = value === '' ? 0 : toMinutes(value, unit);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit();
  };

  return (
    <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
      <div className="border-b border-stroke px-6 py-4 dark:border-strokedark">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Clock size={20} />
          </span>
          <div>
            <h3 className="font-semibold text-black dark:text-white">
              Configuración de token de acceso
            </h3>
            <p className="text-sm text-body dark:text-bodydark">
              Define durante cuánto tiempo serán válidos los nuevos access
              tokens JWT.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} noValidate className="space-y-6 p-6">
        <fieldset disabled={loading} className="space-y-6">
          <div>
            <label
              htmlFor="token-duration"
              className="mb-2.5 block font-medium text-black dark:text-white"
            >
              Duración del token <span className="text-meta-1">*</span>
            </label>
            <div className="flex flex-col gap-3 sm:flex-row">
              <input
                id="token-duration"
                type="number"
                min={unit === 'minutes' ? 5 : 5 / 60}
                max={unit === 'minutes' ? 10080 : 168}
                step={unit === 'minutes' ? 1 : 0.01}
                value={value}
                onChange={(event) =>
                  onValueChange(
                    event.target.value === '' ? '' : Number(event.target.value)
                  )
                }
                aria-invalid={!!error}
                aria-describedby={error ? 'token-duration-error' : undefined}
                className="w-full rounded border border-stroke bg-transparent px-5 py-3 outline-none transition focus:border-primary disabled:cursor-not-allowed disabled:opacity-60 dark:border-form-strokedark dark:bg-form-input dark:text-white sm:flex-1"
              />
              <select
                aria-label="Unidad de duración"
                value={unit}
                onChange={(event) =>
                  onUnitChange(event.target.value as TokenDurationUnit)
                }
                className="rounded border border-stroke bg-transparent px-5 py-3 outline-none transition focus:border-primary disabled:cursor-not-allowed disabled:opacity-60 dark:border-form-strokedark dark:bg-form-input dark:text-white sm:w-48"
              >
                <option value="minutes">Minutos</option>
                <option value="hours">Horas</option>
              </select>
            </div>
            {error && (
              <p
                id="token-duration-error"
                role="alert"
                className="mt-2 text-sm text-meta-1"
              >
                {error}
              </p>
            )}
          </div>

          {value !== '' && durationMinutes > 0 && (
            <p className="rounded-md bg-primary/5 px-4 py-3 text-sm font-medium text-primary">
              Los nuevos tokens expirarán después de{' '}
              {readableDuration(durationMinutes)}.
            </p>
          )}

          <div className="flex gap-3 rounded-md border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-body dark:text-bodydark">
            <Info size={18} className="mt-0.5 shrink-0 text-primary" />
            <span>
              Este cambio solo aplica a tokens emitidos posteriormente. La
              sesión actual y los tokens ya emitidos no se cerrarán ni
              modificarán.
            </span>
          </div>

          <p className="text-sm text-body dark:text-bodydark">
            <span className="font-medium text-black dark:text-white">
              Última actualización:
            </span>{' '}
            {formatUpdatedAt(updatedAt)}
          </p>

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onReset}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded border border-stroke bg-white px-5 py-2.5 font-medium text-black transition hover:bg-gray-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-strokedark dark:bg-boxdark dark:text-white dark:hover:bg-meta-4"
            >
              <RotateCcw size={17} />
              Restablecer a 8 horas
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded bg-primary px-5 py-2.5 font-medium text-white transition hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Save size={17} />
              {loading ? 'Guardando...' : 'Guardar configuración'}
            </button>
          </div>
        </fieldset>
      </form>
    </div>
  );
};

export default TokenConfigurationForm;
